'use strict';

import * as Onyx from '../../lib/onyx.min.js';

import {default as Background} from '../../entities/background';
import {default as Player} from '../../entities/player';
import {default as Slingshot} from '../../entities/slingshot';
import {default as Barrel} from '../../entities/barrel';
import {default as BarrelSideways} from '../../entities/barrel-sideways';
import {default as Beam} from '../../entities/beam';
import {default as Bubbles} from '../../entities/bubbles';
import {default as Chest} from '../../entities/chest';
import {default as Crate} from '../../entities/crate';
import {default as Kelp} from '../../entities/kelp';
import {default as Star} from '../../entities/star';

const ENTITY_CLASSES = { Background, Slingshot, Barrel, BarrelSideways, Beam, Bubbles, Chest, Crate, Kelp, Star };

import {default as Levels} from '../../scripts/levels';

import {default as FrameBufferTexture2DCaustic} from '../../shaders/caustic';
import {default as FrameBufferTexture2DGodRays} from '../../shaders/godrays';

const MATTER_DEBUG_VIEW = true;
const PHYSICS_ENABLED = true;
const MIN_LOAD_AUDIO_ERROR_COUNT = 3; // Number of times to try grabbing the audio before falling back to offline mode
const LOAD_AUDIO_TIMEOUT = 3000;
const MAX_GAME_TIME = 3 * 60 * 1000; // 3 Minutes

const MUSIC_LEVEL_GAMEPLAY = 0.75;
const MUSIC_LEVEL_PEDAGOGIC = 0.25;

const FIRST_LEVEL = 0;
const MAX_WAIT_TIME = 10000; // Time to wait in a round while things settle before just moving on

const GRAVITY_SCALE = 0.75;
const WORLD_SLEEP_TOLERANCE = 0.0015; // How much things can be moving before we consider it "asleep" and move on

const AMMO_DENSITY_SUCCESS = 0.002;
const AMMO_DENSITY_DUD = 0.000001;
const AMMO_RESTITUTION_SUCCESS = 0.5;
const AMMO_RESTITUTION_DUD = 0.0;
const AMMO_BONUS_SCALE = 2;

// Note: Categories are what "group" they're in, masks are what "groups" they can collide with
const COLLISION_CATEGORY_GROUND = 0x0001;
const COLLISION_CATEGORY_MOUSE = 0x0008;
const COLLISION_MASK_GROUND = 0x0002 | 0x0004; // OCTOPUS | TAKO
const COLLISION_MASK_MOUSE = 0x0002; // OCTOPUS

const CAMERA_PULLBACK_RATIO = 2; // Higher number is less pullback
const CAMERA_SHAKE_FORCE = 2;
const CAMERA_SHAKE_DECAY = 0.95;
const CAMERA_SHAKE_TIME = 5000;

export default class SceneGame extends Onyx.Scene {
    constructor(){
        super({
            template: '@@import routes/game/index.html',
            clock: new Onyx.Clock()
        });

        this.physicsEnabled = true;

        window.debug = false;

        this.timeUp = false;
        this.roundCount = 0;

        this.levelsPlayedBag = []; // This is so they get a random level each time
    }

    onload(){
        super.onload();

        this.score = 0;
        this.correctInARow = 0;
        this.roundOver = true;
        this.timeUp = false;
        this.chests = [];
        this.items = [];
        this.isGameover = false;
        // this.physicsEnabled = false;

        this.loadAudioErrorCount = 0;

        document.getElementById("student-name").innerHTML = document.getElementById("student-name").innerHTML.replace("${centralStation.player.first_name}", centralStation.player.first_name);
		document.getElementById("student-name").innerHTML = document.getElementById("student-name").innerHTML.replace("${centralStation.player.last_name}", centralStation.player.last_name);

        let precision = this.viewports[0].webgl.getShaderPrecisionFormat(this.viewports[0].webgl.FRAGMENT_SHADER, this.viewports[0].webgl.HIGH_FLOAT);
        if(precision.rangeMax < 15){
            // Pull from title textures to save on memory
            this.caustic = game.router.map["title"].module.caustic;
            this.godrays = game.router.map["title"].module.godrays;
        }else{
            this.caustic = new FrameBufferTexture2DCaustic();
            this.viewports[0].addFrameBufferTexture(this.caustic);
            this.godrays = new FrameBufferTexture2DGodRays();
            this.viewports[0].addFrameBufferTexture(this.godrays);
        }

        this.camera = new Onyx.Camera({followType: "EaseOut", stiffness: 0.2});
        this.add(this.camera);
        this.camera.limit.minimum.x = 300;
        this.camera.limit.minimum.y = 180;
        this.camera.limit.maximum.y = 475;
        this.viewports[0].bindToCamera(this.camera);

        // Setup music
        game.music['game'].play();
        game.music['game'].volume(.5);

        this.getPhysicsWorldCoordinates();

        this.setupMatter();
        this.setupScene();

        this.loadLevel(FIRST_LEVEL);

        // Onyx.ImportModelFromFBX.clearCache();
        // Onyx.ImportModelFromOBJ.clearCache();

        this.clock.stop();
        window.setTimeout(() => { // Give the browser a second to breathe
            game.hideLoadingMask();
            this.showPedagogic();

            this.clock.start();
        }, 1500);

        this.setTimeout(() => {
            this.timeUp = true;
        }, MAX_GAME_TIME);

        // Rotate camera on device rotation
        if ( window.DeviceMotionEvent ) {
            window.ondeviceorientation = (event) => {
                let alpha = event.alpha / 180*Math.PI;
                let beta = event.beta / 180*Math.PI;
                let gamma = event.gamma / 180*Math.PI;
                let scale = 0.3;

                // Prevent camera from flipping
                if(beta < -2 || beta > 2) return;

                this.camera.transform.rotateToXYZ(0, beta * scale);
            }
        }

        // Should do this on window resize
        this.matterRender.canvas.width = this.viewports[0].contextElement.width;
        this.matterRender.canvas.height = this.viewports[0].contextElement.height;
        this.matterRender.options.width = this.viewports[0].contextElement.width;
        this.matterRender.options.height = this.viewports[0].contextElement.height;
    }

    onunload(){
        super.onunload();

        delete this.matterEngine;
        this.takos = [];
        this.bankfish = [];
    }

    update(dt){
        super.update(dt);

        if(this.isGameover) return;

        if(this.physicsEnabled && PHYSICS_ENABLED) Matter.Engine.update(this.matterEngine, dt);

        if(!this.roundOver && this.checkWorldAsleep()){
            this.roundOver = true;
            this.remove(this.timerMoveon);

            if(this.timeUp && this.player.isLanded){
                this.isGameover = true;

                game.showLoadingMask();

                this.setTimeout(() => {
                    router.navigate("gameover", {score: this.score});
                }, 1000);

                return;
            }

            this.setTimeout(() => {
                if(this.checkAllDefeated()){
                    router.modal("level-complete", () => {
                        game.showLoadingMask();

                        this.setTimeout(() => {
                            this.unloadLevel();
                            this.loadLevel();

                            this.clock.stop();
                            window.setTimeout(() => { // Give the browser some time to breathe
                                game.hideLoadingMask();

                                this.clock.start();

                                this.showPedagogic();
                            }, 1000);
                        }, 1000);
                    }, () => {}, {
                        score: this.score
                    }, {view: "modal"});
                }else{
                        this.remove(this.player);

                        this.showPedagogic(true);
                }
            }, 3000);
        }

        if(this.slingshot && !this.slingshot.launched && this.player && this.player.physicsEnabled){
            this.camera.followOffset[2] = 600 + (this.slingshot.stretch / CAMERA_PULLBACK_RATIO);
        }
    }

    render(...args){
        super.render(...args);

        this.getPhysicsWorldCoordinates();

        // Rescale MatterMouse because the viewport isn't always the size of the world height
        Matter.Mouse.setScale(this.matterMouse, {x: this.physicsWorldView.width / this.viewports[0].contextElement.width, y: this.physicsWorldView.height / this.viewports[0].contextElement.height});

        Matter.Render.lookAt(this.matterRender, {
            min: { x: this.physicsWorldView.min.x, y: -this.physicsWorldView.max.y },
            max: { x: this.physicsWorldView.max.x, y: -this.physicsWorldView.min.y }
        });

        if(MATTER_DEBUG_VIEW && window.debug){
            Matter.Render.world(this.matterRender);
        }
    }

    showPedagogic(shortSequence){
        document.getElementById("scene-game").classList.add("blurry");

        game.music['game'].fade(MUSIC_LEVEL_GAMEPLAY, MUSIC_LEVEL_PEDAGOGIC, 1000);

        this.camera.follow(null);
        this.camera.transform.translateTo(this.focus[0], this.focus[1], this.focus[2]);

        this.viewports[0].evaluate(); // iOS 11 needs a refresher once in awhile.

        router.modal("pedagogic", () => {
            this.viewports[0].evaluate(); // iOS 11 needs a refresher once in awhile.

            this.correctInARow++;
            // this.loadAmmo(AMMO_DENSITY_SUCCESS, AMMO_RESTITUTION_SUCCESS, (this.correctInARow !== 0 && this.correctInARow % 3 === 0)?AMMO_BONUS_SCALE:1);
            this.loadAmmo(AMMO_DENSITY_SUCCESS, AMMO_RESTITUTION_SUCCESS, 1);

            game.music['game'].fade(MUSIC_LEVEL_PEDAGOGIC, MUSIC_LEVEL_GAMEPLAY, 1000);
            document.getElementById("scene-game").classList.remove("blurry");

            this.playSceneStartSequence(shortSequence);
        }, () => {
            this.correctInARow = 0;

            // this.loadAmmo(AMMO_DENSITY_DUD, AMMO_RESTITUTION_DUD, 1, true);
            // this.game.sfx['WordIncorrect'].play(0, 0, true);

            // Give them another word
            this.showPedagogic(shortSequence);
        }, null, {view: "modal"});
    }

    getPhysicsWorldCoordinates(){
        let min = this.camera.unproject(this.viewports[0], 0, 0, 1);
        let max = this.camera.unproject(this.viewports[0], this.viewportElements[0].width, this.viewportElements[0].height, 1);

        this.physicsWorldView = {
            min: {
                x: min[0],
                y: min[1]
            },
            max: {
                x: max[0],
                y: max[1]
            },
            width: max[0] - min[0],
            height: max[1] - min[1],
        }
    }

    setupMatter(){
        this.matterEngine = Matter.Engine.create({
            // enableSleeping: true
            positionIterations: 6,
            velocityIterations: 4,
            constraintIterations: 2,
        });

        // Mouse
        this.matterMouse = Matter.Mouse.create(this.viewports[0].contextElement);
        this.mouseConstraint = Matter.MouseConstraint.create(this.matterEngine, {
            mouse: this.matterMouse,
            collisionFilter: {
                category: COLLISION_CATEGORY_MOUSE,
                mask: COLLISION_MASK_MOUSE
            },
            constraint: {
                stiffness: 0.2,
                render: {
                    visible: true
                }
            }
        });

        Matter.Events.on(this.mouseConstraint, "enddrag", () => {
            this.slingshot.launch();
        });

        // Collision
        Matter.Events.on(this.matterEngine, 'collisionStart', (event) => {
            for(let pair of event.pairs){
                if(!pair.bodyA.prevVelocity || !pair.bodyB.prevVelocity) continue;

                var bodyAMomentum = Matter.Vector.mult(pair.bodyA.prevVelocity, pair.bodyA.mass);
                var bodyBMomentum = Matter.Vector.mult(pair.bodyB.prevVelocity, pair.bodyB.mass);
                var relativeMomentum = Matter.Vector.sub(bodyAMomentum, bodyBMomentum);

                if(pair.bodyA.parentEntity) pair.bodyA.parentEntity.emit("oncollide", {event: event, relativeMomentum: Matter.Vector.magnitude(relativeMomentum)});
                if(pair.bodyB.parentEntity) pair.bodyB.parentEntity.emit("oncollide", {event: event, relativeMomentum: Matter.Vector.magnitude(relativeMomentum)});
                // console.log(Matter.Vector.magnitude(relativeMomentum));
            }

        });

        // Add to the world
        Matter.World.add(this.matterEngine.world, [this.mouseConstraint]);

        if(MATTER_DEBUG_VIEW){
            this.matterRender = Matter.Render.create({
                element: document.getElementById('matter-debug'),
                engine: this.matterEngine,
                mouse: this.matterMouse,
                options: {
                    width: this.viewportElements[0].width,
                    height: this.viewportElements[0].height,
                    // pixelRatio: 10,
                    wireframeBackground: 'rgba(0,0,0,0.1)',
                    showSleeping: true,
                    showDebug: false,
                    showBroadphase: false,
                    showBounds: false,
                    showVelocity: true,
                    showCollisions: false,
                    showSeparations: false,
                    showAxes: false,
                    showPositions: false,
                    showAngleIndicator: true,
                    showIds: false,
                    showShadows: false,
                    showVertexNumbers: false,
                    showConvexHulls: false,
                    showInternalEdges: false,
                    showMousePosition: true
                }
            });
        }
    }

    setupScene(){
        // create ground bodies
        var width = this.physicsWorldView.width;
        var height = this.physicsWorldView.height;

        this.floor = Matter.Bodies.rectangle(0, 250 + 50, 20000, 500, { isStatic: true, collisionFiter: { category: COLLISION_CATEGORY_GROUND, mask: COLLISION_MASK_GROUND }, friction: 0.90 });
        this.floor.prevVelocity = { x: 0, y: 0 };
        this.rightWall = Matter.Bodies.rectangle(0, 0, 100, 2000, { isStatic: true, collisionFiter: { category: COLLISION_CATEGORY_GROUND, mask: COLLISION_MASK_GROUND } });
        this.rightWall.prevVelocity = { x: 0, y: 0 };

        Matter.World.add(this.matterEngine.world, [
            this.floor,
            this.rightWall
        ]);

        this.ambientLightColor.set(0.5, 0.5, 0.5);
        this.directionalLightVector.set([-0.25, 0.8, 0.75]);
        this.fogColor.set(64 / 255, 130 / 255, 131 / 255);
        this.fogDensity = 1.25;
    }

    chooseNewRandomLevel(){
        let level = Math.floor(Math.random() * Levels.length);

        // Reset bag if we've already played all of them
        if(this.levelsPlayedBag.length === Levels.length) this.levelsPlayedBag = [];

        // Try again if we've already picked this level
        if(this.levelsPlayedBag.indexOf(level) > -1) return this.chooseNewRandomLevel();

        return level;
    }

    loadLevel(level = this.chooseNewRandomLevel()){
        this.roundCount++;

        this.levelsPlayedBag.push(level);
        console.log(level, this.levelsPlayedBag);

        this.focus = Levels[level].focus;
        this.camera.limit.maximum.x = this.focus[0] + 500;
        this.camera.transform.translateTo(this.focus[0], this.focus[1], this.focus[2]);

        Matter.Body.setPosition(this.rightWall, {x: this.focus[0] + 1280, y: 0});

        for(let entity of Levels[level].entities){
            let newEntity = new ENTITY_CLASSES[entity.class](entity.position, entity.scale, entity.rotation, {
                matterEngine: this.matterEngine,
                godrays: this.godrays,
                caustic: this.caustic
            });
            this.add(newEntity);

            this.items.push(newEntity);

            if(entity.class == "Slingshot"){
                this.slingshot = newEntity;
            }

            if(entity.class == "Chest"){
                this.chests.push(newEntity);
            }
        }
    }

    playSceneStartSequence(shortVersion){
        this.camera.stiffness = 0.05;

        if(!shortVersion){
            let followPoint = new Onyx.Entity();
            this.camera.follow(followPoint, [0,0,0]);

            // Position at focus point
            followPoint.transform.translateTo(this.focus[0], this.focus[1], this.focus[2]);
        }

        this.setTimeout(() => {
            // Position at slingshot
            this.camera.follow(this.slingshot, [175, 50, 600]);

            // Make character swim from left
            this.player.physicsEnabled = false;
            // this.player.components[0].animationClips[0].rate = 5;

            //TODO: Cleanup how tweens are created
            let t = new Onyx.Tween();
            t.easeIn(this.player.transform, "x", this.player.transform.x, 1500, () => {
                // Zoom out
                // this.camera.follow(this.player, [175, 50, 600]);
                this.camera.stiffness = 0.2;
                this.player.components[0].animationClips[0].rate = 1;

                this.player.physicsEnabled = true;

                this.player.enableDrag();
            });
            t.from = -300;
            this.addComponent(t);
        }, (!shortVersion)?2000:0);
    }

    unloadLevel(){
        this.remove(this.player);

        for(let entity of this.items){
            this.remove(entity);
        }
    }

    showHelperHand(){
        this.helperHand = new Onyx.Sprite("res/textures/octo_fingerHand.png", {pivot: [0.5, 0.5]});
        this.helperHand.transform.scale(32, 32);
        this.addComponent(this.helperHand);

        // Helper hand animation
        this.helperHandTimer = this.setInterval(() => {
            this.helperHand.visible = true;
            this.helperHand.transform.translateTo(this.slingshot.transform.x, this.slingshot.transform.y - 50, 2);
            let helperHandTweenX = new Onyx.Tween();
            helperHandTweenX.easeIn(this.helperHand.transform, "x", this.slingshot.transform.x - 100, 750);
            this.add(helperHandTweenX);

            let helperHandTweenY = new Onyx.Tween();
            helperHandTweenY.easeIn(this.helperHand.transform, "y", this.slingshot.transform.y - 100, 750, () => {
                this.helperHand.visible = false;
                this.remove(helperHandTweenX);
                this.remove(helperHandTweenY);
            });
            this.add(helperHandTweenY);
        }, 1000);
    }

    // @Onyx.handles("mousedown", {elementId: "main"})
    // @Onyx.handles("touchstart", {elementId: "main"})
    removeHelperHand(){
        this.helperHandTimer.enabled = false;
        this.helperHand.visible = false;
    }

    @Onyx.handles("score")
    addScore(entity){
        this.score += 1;
        document.getElementById("score").innerHTML = Math.round(this.score);

        let star = new Star([entity.transform.x, entity.transform.y], 1);
        this.add(star);
        this.items.push(star);
    }

    @Onyx.handles("gameover")
    gameover(){
        router.navigate("gameover", {score: Math.round(this.score)});

        // game.sfx["sndGameOver"].play();
    }

    checkWorldAsleep(){
        let isAsleep = true;

        if(this.moveon) return true;

        this.matterEngine.world.bodies.forEach((body) => {
            if(!body.isSensor && !(body.parentEntity && !body.parentEntity.waitForSleep) && (Math.abs(body.velocity.x) > WORLD_SLEEP_TOLERANCE || Math.abs(body.velocity.y) > WORLD_SLEEP_TOLERANCE)){
                isAsleep = false;
                return;
            }
        })

        return isAsleep;
    }

    checkAllDefeated(){
        for(let chest of this.chests){
            // If still exists and it hasn't been flagged as open
            if(this.entities.indexOf(chest) > -1 && !chest.open){
                return false;
            }
        }

        return true;
    }

    loadAmmo(density, restitution, scale){
		// Player
        this.player = new Player([this.slingshot.transform.x, this.slingshot.transform.y, 0], scale, 0, {matterEngine: this.matterEngine});
        this.add(this.player);

        this.slingshot.launched = false;
    }

    @Onyx.handles("launched")
    startRound(){
        this.roundOver = false;

        this.camera.followEntity = this.player;

        this.timerMoveon = this.setTimeout(() => {
            this.moveon = true;
        }, MAX_WAIT_TIME);
    }

    shakeCamera(){
        let shake = CAMERA_SHAKE_FORCE;
        let polarity = 1;
        this.setInterval(() => {
            var angle = (Math.random() * Math.PI) * polarity;
            var px = Math.cos(angle) * shake;
            var py = Math.sin(angle) * shake;

            this.camera.transform.x += px;
            this.camera.transform.y += py;

            shake *= CAMERA_SHAKE_DECAY;
            polarity = (polarity === 1)?-1:1;
        }, 0, CAMERA_SHAKE_TIME / (1000/60));
    }
}
